import { LayoutTemplate } from 'lucide-react'
import { type ComponentProps, useCallback, useMemo, useState } from 'react'
import * as m from '~/paraglide/messages'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'
import { FlyerGenerator } from './flyer-generator'

interface FlyerGeneratorDialogProps {
  qrData: string
  label: string
  coverUrl?: string | null
  images?: { url: string }[]
  description?: string | null
  triggerClassName?: string
  triggerVariant?: ComponentProps<typeof Button>['variant']
  triggerLabel?: string
}

export function FlyerGeneratorDialog({
  qrData,
  label,
  coverUrl,
  images,
  description,
  triggerClassName = 'w-full',
  triggerVariant = 'outline',
  triggerLabel,
}: FlyerGeneratorDialogProps) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  // A fresh array identity here would re-trigger the generator's preload effect.
  const presetImages = useMemo(
    () =>
      Array.from(
        new Set(
          [coverUrl, ...(images ?? []).map((image) => image.url)].filter(
            (url): url is string => !!url,
          ),
        ),
      ),
    [coverUrl, images],
  )

  // Radix unmounts the generator on close, which would strand a paid generation:
  // the credits are spent but the result never reaches the user.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && busy) return
      setOpen(next)
    },
    [busy],
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} className={triggerClassName}>
          <LayoutTemplate className="mr-2 h-4 w-4" />
          {triggerLabel ?? m.flyerTool()}
        </Button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={!busy}
        // Radix would focus the first Format button, where a stray Enter silently
        // changes the format.
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="max-h-[85vh] overflow-y-auto sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>{m.flyerTool()}</DialogTitle>
          <DialogDescription>{m.flyerDialogDescription({ name: label })}</DialogDescription>
        </DialogHeader>
        <FlyerGenerator
          presetQrData={qrData}
          presetImages={presetImages}
          presetLabel={label}
          presetTitle={label}
          presetDescription={description ?? undefined}
          onBusyChange={setBusy}
        />
      </DialogContent>
    </Dialog>
  )
}
